import { TableColumn } from '../../types/table';
import { AdditionalFeesMap, Car, FeeType } from '../../types';

export interface CarsColumnsOptions {
  includeVoyageColumn?: boolean;
}

/**
 * Calculates the sum of additional fees for a given fee type.
 * @param fees - The fees map from a car or related car
 * @param targetTypes - Fee types to include in the sum ('F', 'C', or 'B')
 */
function sumAdditionalFees(fees: AdditionalFeesMap | undefined, targetTypes: FeeType[]): number {
  if (!fees) return 0;
  return Object.values(fees).reduce((sum, fee) => {
    if (targetTypes.includes(fee.type)) {
      return sum + fee.amount;
    }
    return sum;
  }, 0);
}

/**
 * Calculates total fees for a car including its related cars.
 * @param car - The car to calculate fees for
 * @param targetTypes - Fee types to include ('F' and 'B' for freight, 'C' and 'B' for collect)
 */
function calculateTotalFees(car: Car, targetTypes: FeeType[]): number {
  let total = sumAdditionalFees(car.additional_fees, targetTypes);
  if (car.related_cars) {
    for (const rc of car.related_cars) {
      total += sumAdditionalFees(rc.additional_fees, targetTypes);
    }
  }
  return total;
}

/**
 * Standard column definitions for cars table.
 * Used by CarsTable with configurable voyage column visibility.
 * Width values are optimized to fit on a typical laptop screen (~1366px) without horizontal scrolling.
 * Chassis column fits max width "WWWWWWWWWWWWWWWWW" (17 chars) in monospace.
 *
 * @param options - Configuration options for column visibility
 * @returns Array of table column definitions
 */
export function getCarsColumns(options: CarsColumnsOptions = {}): TableColumn<Car>[] {
  const { includeVoyageColumn = false } = options;

  return [
    ...(includeVoyageColumn ? [{
      key: 'voyage',
      label: 'Voyage',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 82,
      accessor: (car: Car) => car.voyage_code ?? car.voyage?.voyage_code ?? '-',
    }] : []),
    {
      key: 'pod',
      label: 'POD',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 58,
      accessor: (car: Car) => car.port_code ?? car.port?.code ?? '-',
    },
    {
      key: 'ship',
      label: 'Ship',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 52,
      accessor: (car: Car) => car.shipper_code ?? car.shipper?.code ?? '-',
    },
    {
      key: 'shipper',
      label: 'Shipper',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 115,
      accessor: (car: Car) => car.shipper?.shortname ?? car.shipper_name ?? car.shipper?.name ?? '-',
    },
    {
      key: 'booking_ref',
      label: 'Book.Ref',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 100,
      accessor: (car: Car) => {
        const bookingRef = car.booking_reference;
        return bookingRef && bookingRef !== 'N/A' ? bookingRef : '-';
      },
    },
    {
      key: 'consignee',
      label: 'Consignee',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 120,
      accessor: (car: Car) => car.consignee_name ?? '-',
    },
    {
      key: 'make_model',
      label: 'Make/Model',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 145,
      accessor: (car: Car) => {
        // Use denormalized fields first, fall back to nested objects, then manual entry
        const make = car.make_name ?? car.make?.name ?? '';
        const model = car.model_name ?? car.model?.name ?? '';
        if (make && model) return `${make}/${model}`;
        if (make || model) return make || model;
        return car.manual_make_model || '-';
      },
    },
    {
      key: 'cat',
      label: 'Cat',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 54,
      accessor: (car: Car) => car.category ?? car.model?.type ?? '-'
    },
    {
      key: 'chs_no',
      label: 'Chassis',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 132,  // Fits 17 chars (VIN) in monospace text-xs
      accessor: (car: Car) => car.chassis_number ?? '-'
    },
    {
      key: 'c',
      label: 'C',
      sortable: true,
      searchable: false,
      filterable: true,
      width: 38,
      accessor: (car: Car) => car.prepaid_or_collect ?? '-'
    },
    { key: 'loaded', label: 'Ld', sortable: false, searchable: false, filterable: true, width: 44 },
    {
      key: 'freight',
      label: 'Frt',
      sortable: true,
      searchable: false,
      filterable: true,
      width: 56,
      accessor: (car: Car) => (car.freight ?? 0) + calculateTotalFees(car, ['F', 'B'])
    },
    {
      key: 'collect',
      label: 'Col',
      sortable: true,
      searchable: false,
      filterable: true,
      width: 56,
      accessor: (car: Car) => (car.collect ?? 0) + calculateTotalFees(car, ['C', 'B'])
    },
    {
      key: 'age',
      label: 'Age',
      sortable: true,
      searchable: false,
      filterable: true,
      width: 44,
      accessor: (car: Car) => {
        if (!car.created_at) return '-';
        const fmt = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Beirut' });
        const createdDate = fmt(new Date(car.created_at));
        const todayDate = fmt(new Date());
        const diffMs = new Date(todayDate).getTime() - new Date(createdDate).getTime();
        return Math.round(diffMs / (1000 * 60 * 60 * 24));
      }
    },
    {
      key: 'ref_no',
      label: 'Ref',
      sortable: true,
      searchable: true,
      filterable: true,
      width: 62,
      accessor: (car: Car) => car.reference_number ?? '-'
    },
  ];
}

/**
 * Set of column keys that should use monospace font
 */
export const MONOSPACE_COLUMNS = new Set(['chs_no']);
